import MotionController from "../lib/motion_controller"
import { gsap, ScrollTrigger, observeVisibility } from "../lib/motion"

// Plays a code-rendered product mockup like a screen recording.
//
// The markup is server-rendered in its *final* state (what no-JS and reduced-motion visitors
// see). Descendants opt into the recording with data attributes:
//
//   data-demo-step="in"            action(s), space separated for several on one element
//   data-demo-at="1.2"             GSAP position per action (absolute seconds, "<", "+=0.2"…)
//   data-demo-duration="0.4"       optional duration per action
//
// Actions: in, out, pop, type (data-demo-text, data-demo-cps), stream (token-by-token, keeps
// inline <sup> citations), check (pending → working → done), class (data-demo-class),
// count (data-demo-to/-prefix/-suffix), progress (data-demo-axis="y"), draw (SVG stroke).
//
// Playback is chosen with data-demo-trigger-value: "load" | "view" | "manual"
// (manual demos listen for a `demo:play` event, dispatched by scroll-scene).
// data-demo-scrub-value ties the timeline to scroll on desktop instead.
export default class extends MotionController {
  static targets = [ "stage", "replay" ]
  static values = {
    trigger: { type: String, default: "view" },
    delay: { type: Number, default: 0 },
    start: { type: String, default: "top 70%" },
    loop: Boolean,
    loopDelay: { type: Number, default: 4 },
    speed: { type: Number, default: 1 },
    scrub: Boolean,
    scrubTrigger: String,
    scrubStart: { type: String, default: "top top" },
    scrubEnd: { type: String, default: "+=200%" }
  }

  connect() {
    // A scroll-scene may ask a not-yet-started demo to play; start it and honor the request.
    this.playBeforeStart = () => {
      if (this.mm) return
      this.start()
      this.playDemo?.()
    }
    this.element.addEventListener("demo:play", this.playBeforeStart)
    super.connect()
  }

  disconnect() {
    this.element.removeEventListener("demo:play", this.playBeforeStart)
    super.disconnect()
  }

  animate({ motion, desktop }, context) {
    if (!motion) return

    const hooks = []
    const tl = this.build(hooks)
    this.timeline = tl
    this.hooks = hooks
    this.element.classList.add("is-ready")

    const stage = this.hasStageTarget ? this.stageTarget : this.element
    let loopCall = null
    let visible = true
    let wantsPlayback = false

    const play = () => {
      loopCall?.kill()
      gsap.killTweensOf(stage)
      gsap.set(stage, { autoAlpha: 1 })
      hooks.forEach((h) => h.reset?.())
      wantsPlayback = true
      tl.restart()
      if (!visible) tl.pause()
    }

    const restartLoop = () => {
      gsap.to(stage, {
        autoAlpha: 0,
        duration: 0.5,
        ease: "power2.out",
        onComplete: play
      })
    }

    tl.eventCallback("onComplete", () => {
      if (!this.loopValue) { wantsPlayback = false; return }
      loopCall = gsap.delayedCall(this.loopDelayValue, restartLoop)
      if (!visible) loopCall.pause()
    })

    this.playDemo = play
    this.pauseDemo = () => { wantsPlayback = false; tl.pause(); loopCall?.kill() }

    const scrubbed = this.scrubValue && desktop
    if (scrubbed) {
      const trigger = this.hasScrubTriggerValue ? this.element.closest(this.scrubTriggerValue) : this.element
      ScrollTrigger.create({
        trigger: trigger || this.element,
        start: this.scrubStartValue,
        end: this.scrubEndValue,
        scrub: 0.8,
        animation: tl
      })
    } else if (this.triggerValue === "load") {
      gsap.delayedCall(this.delayValue, play)
    } else if (this.triggerValue === "view") {
      ScrollTrigger.create({ trigger: this.element, start: this.startValue, once: true, onEnter: play })
    }

    const stopObserving = scrubbed ? null : observeVisibility(this.element, (isVisible) => {
      visible = isVisible
      if (!wantsPlayback) return
      if (isVisible) { tl.resume(); loopCall?.resume() } else { tl.pause(); loopCall?.pause() }
    })

    const onPlay = () => play()
    const onPause = () => this.pauseDemo()
    this.element.addEventListener("demo:play", onPlay)
    this.element.addEventListener("demo:pause", onPause)
    this.replayTargets.forEach((button) => { button.hidden = scrubbed })

    return () => {
      stopObserving?.()
      loopCall?.kill()
      gsap.killTweensOf(stage)
      gsap.set(stage, { clearProps: "opacity,visibility" })
      this.element.removeEventListener("demo:play", onPlay)
      this.element.removeEventListener("demo:pause", onPause)
      this.replayTargets.forEach((button) => { button.hidden = true })
      this.element.classList.remove("is-ready")
      hooks.reverse().forEach((h) => h.restore?.())
      this.timeline = null
      this.playDemo = null
      this.pauseDemo = null
    }
  }

  replay() {
    this.playDemo?.()
  }

  build(hooks) {
    const tl = gsap.timeline({ paused: true, defaults: { ease: "expo.out" } })
    tl.timeScale(this.speedValue)
    const seen = new WeakSet()

    this.stepElements().forEach((el) => {
      const actions = el.dataset.demoStep.trim().split(/\s+/)
      const positions = (el.dataset.demoAt || "").trim().split(/\s+/)
      const durations = (el.dataset.demoDuration || "").trim().split(/\s+/)

      actions.forEach((action, index) => {
        const run = ACTIONS[action]
        if (!run) return
        const opts = {
          at: positions[index] || "+=0.1",
          duration: parseFloat(durations[index]),
          first: !seen.has(el)
        }
        seen.add(el)
        const hook = run(tl, el, opts)
        if (hook) hooks.push(hook)
      })
    })

    return tl
  }

  stepElements() {
    return Array.from(this.element.querySelectorAll("[data-demo-step]")).filter((el) => {
      return el.closest("[data-controller~='demo']") === this.element
    })
  }
}

const num = (value, fallback) => {
  const parsed = parseFloat(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

const dur = (opts, fallback) => (Number.isFinite(opts.duration) ? opts.duration : fallback)

// Deterministic jitter so scrubbing back and forth always renders the same frame.
const noise = (i) => {
  const x = Math.sin(i * 12.9898 + 78.233) * 43758.5453
  return x - Math.floor(x)
}

// Monotonic ease with a slight rhythm, so typing reads as human rather than mechanical.
const typingEase = (p) => Math.min(1, Math.max(0, p + Math.sin(p * 38) * 0.012))

function tokenize(root) {
  const tokens = []
  const walk = (node) => {
    Array.from(node.childNodes).forEach((child) => {
      if (child.nodeType === Node.TEXT_NODE) {
        const fragment = document.createDocumentFragment()
        child.textContent.split(/(\s+)/).forEach((part) => {
          if (!part) return
          if (/^\s+$/.test(part)) {
            fragment.appendChild(document.createTextNode(part))
            return
          }
          const span = document.createElement("span")
          span.className = "demo-token"
          span.textContent = part
          fragment.appendChild(span)
          tokens.push(span)
        })
        child.replaceWith(fragment)
      } else if (child.nodeType === Node.ELEMENT_NODE) {
        if (child.matches("sup, svg, img, [data-demo-token]")) tokens.push(child)
        else walk(child)
      }
    })
  }
  walk(root)
  return tokens
}

const ACTIONS = {
  in(tl, el, opts) {
    const y = num(el.dataset.demoY, 8)
    const x = num(el.dataset.demoX, 0)
    const vars = { autoAlpha: 1, x: 0, y: 0, duration: dur(opts, 0.6) }
    if (opts.first) tl.fromTo(el, { autoAlpha: 0, x, y }, vars, opts.at)
    else tl.to(el, vars, opts.at)
  },

  out(tl, el, opts) {
    tl.to(el, { autoAlpha: 0, duration: dur(opts, 0.25), ease: "power2.out" }, opts.at)
  },

  pop(tl, el, opts) {
    tl.fromTo(el,
      { autoAlpha: 0, scale: num(el.dataset.demoScale, 0.92) },
      { autoAlpha: 1, scale: 1, duration: dur(opts, 0.7), immediateRender: opts.first },
      opts.at)
  },

  type(tl, el, opts) {
    const original = el.textContent
    const text = el.dataset.demoText ?? original
    const proxy = { n: 0 }
    const render = () => { el.textContent = text.slice(0, Math.round(proxy.n)) }
    el.textContent = ""
    tl.fromTo(proxy, { n: 0 }, {
      n: text.length,
      duration: dur(opts, text.length / num(el.dataset.demoCps, 30)),
      ease: typingEase,
      onUpdate: render
    }, opts.at)
    return {
      reset: () => { proxy.n = 0; el.textContent = "" },
      restore: () => { el.textContent = original }
    }
  },

  stream(tl, el, opts) {
    const original = el.innerHTML
    const tokens = tokenize(el)
    const rate = num(el.dataset.demoRate, 0.045)
    const offsets = []
    tokens.forEach((_, i) => {
      const previous = i === 0 ? 0 : offsets[i - 1]
      offsets.push(i === 0 ? 0 : previous + rate * (0.35 + noise(i) * 1.3))
    })
    tl.fromTo(tokens, { opacity: 0 }, {
      opacity: 1,
      duration: 0.28,
      ease: "none",
      stagger: (i) => offsets[i]
    }, opts.at)
    return { restore: () => { el.innerHTML = original } }
  },

  check(tl, el, opts) {
    const part = (name) => Array.from(el.querySelectorAll(`[data-check~="${name}"]`))
    const work = dur(opts, 0.7)
    const sub = gsap.timeline({ defaults: { ease: "expo.out" } })
    const add = (targets, fn) => { if (targets.length) fn(targets) }

    add(part("label"), (t) => sub.fromTo(t, { opacity: 0.6 }, { opacity: 1, duration: 0.3 }, 0))
    add(part("pending"), (t) => sub.fromTo(t, { autoAlpha: 1 }, { autoAlpha: 0, duration: 0.2 }, 0))
    add(part("working"), (t) => {
      sub.fromTo(t, { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.2 }, 0)
      sub.to(t, { autoAlpha: 0, duration: 0.2 }, work)
    })
    add(part("done"), (t) => sub.fromTo(t, { autoAlpha: 0, scale: 0.5 }, { autoAlpha: 1, scale: 1, duration: 0.5 }, work))
    add(part("draw"), (t) => sub.fromTo(t, { strokeDashoffset: 1 }, { strokeDashoffset: 0, duration: 0.45, ease: "power2.out" }, work + 0.05))
    add(part("detail"), (t) => sub.fromTo(t, { autoAlpha: 0, y: 4 }, { autoAlpha: 1, y: 0, duration: 0.45 }, work + 0.1))
    tl.add(sub, opts.at)
  },

  class(tl, el, opts) {
    const name = el.dataset.demoClass
    const hadClass = el.classList.contains(name)
    el.classList.remove(name)
    tl.to({}, {
      duration: 0.001,
      onComplete: () => el.classList.add(name),
      onReverseComplete: () => el.classList.remove(name)
    }, opts.at)
    return {
      reset: () => el.classList.remove(name),
      restore: () => el.classList.toggle(name, hadClass)
    }
  },

  count(tl, el, opts) {
    const original = el.textContent
    const to = num(el.dataset.demoTo, parseFloat(original.replace(/[^\d.]/g, "")) || 0)
    const prefix = el.dataset.demoPrefix || ""
    const suffix = el.dataset.demoSuffix || ""
    const format = new Intl.NumberFormat("en-US")
    const proxy = { value: 0 }
    const render = () => { el.textContent = `${prefix}${format.format(Math.round(proxy.value))}${suffix}` }
    render()
    tl.fromTo(proxy, { value: 0 }, { value: to, duration: dur(opts, 1.2), ease: "power2.out", onUpdate: render }, opts.at)
    return {
      reset: () => { proxy.value = 0; render() },
      restore: () => { el.textContent = original }
    }
  },

  progress(tl, el, opts) {
    const prop = el.dataset.demoAxis === "y" ? "scaleY" : "scaleX"
    tl.fromTo(el, { [prop]: 0 }, { [prop]: 1, duration: dur(opts, 1), ease: "none" }, opts.at)
  },

  draw(tl, el, opts) {
    tl.fromTo(el, { strokeDashoffset: 1 }, { strokeDashoffset: 0, duration: dur(opts, 1), ease: "power2.inOut" }, opts.at)
  }
}
